import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  // Helper to get array translations
  const getList = (key) => t(key, { returnObjects: true }) || [];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div {...fadeUp} className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0A2540]/10 mb-6">
            <Shield className="w-8 h-8 text-[#0A2540]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t('legal.privacy.title')}
          </h1>
          <p className="text-slate-500">
            {t('legal.lastUpdated')} : {t('legal.toComplete')}
          </p>
        </motion.div>

        <motion.div 
          {...fadeUp}
          className="bg-white rounded-2xl shadow-soft p-8 md:p-12 space-y-10"
        >
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section1.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.privacy.section1.p1')}
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section1.p2')}
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section2.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section2.intro')}
            </p>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section2.identification')}</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4 ml-4">
              {getList('legal.privacy.section2.idList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section2.professional')}</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4 ml-4">
              {getList('legal.privacy.section2.proList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section2.order')}</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4 ml-4">
              {getList('legal.privacy.section2.orderList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section2.technical')}</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section2.techList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section3.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section3.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section3.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section4.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('legal.privacy.section4.p1')}</strong>
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('legal.privacy.section4.p2')}</strong>
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section4.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section4.p3')}
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section5.title')}
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section5.retention')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section5.retentionText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section5.deletion')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section5.deletionIntro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section5.deletionList').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section5.deletionNote')}
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section6.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section6.intro')}
            </p>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section6.payment')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section6.paymentText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section6.hosting')}</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section6.hostingText')}
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('legal.privacy.section6.legal')}</h3>
            <p className="text-slate-600 leading-relaxed">
              {t('legal.privacy.section6.legalText')}
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section7.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section7.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section7.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section7.note')}
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section8.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section8.intro')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              {getList('legal.privacy.section8.list').map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section8.note')}
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {t('legal.privacy.section9.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t('legal.privacy.section9.intro')}
            </p>
            <div className="bg-slate-50 rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#0A2540]/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#0A2540]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{t('legal.privacy.section9.department')}</p>
                <a href="mailto:hello@ikatone.com" className="text-[#0A2540] hover:underline">
                  hello@ikatone.com
                </a>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mt-4">
              {t('legal.privacy.section9.response')}
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
